import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';
import '../assets/styles/ProgressReports.css';

const ProgressReports = ({ householdId, userRole }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('weekly');
  const [selectedKid, setSelectedKid] = useState('all');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/v1/reports/progress?household_id=${householdId}&period=${period}`;

      if (selectedKid !== 'all') {
        url += `&kid_user_id=${selectedKid}`;
      }

      const response = await axios.get(url);

      setReport(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch progress report:', err);
      setError('Failed to load progress report');
    } finally {
      setLoading(false);
    }
  }, [householdId, period, selectedKid]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const formatCurrency = (amount) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="progress-reports-loading">
        <div className="spinner"></div>
        <p>Loading progress report...</p>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!report) {
    return <div className="no-data">No report data available</div>;
  }

  return (
    <div className="progress-reports">
      <div className="reports-header">
        <div className="header-left">
          <h2>📊 Progress Report</h2>
          <p className="date-range">
            {formatDate(report.start_date)} - {formatDate(report.end_date)}
          </p>
        </div>
        <div className="header-controls">
          <div className="period-selector">
            <button
              className={`period-btn ${period === 'daily' ? 'active' : ''}`}
              onClick={() => setPeriod('daily')}
            >
              Daily
            </button>
            <button
              className={`period-btn ${period === 'weekly' ? 'active' : ''}`}
              onClick={() => setPeriod('weekly')}
            >
              Weekly
            </button>
            <button
              className={`period-btn ${period === 'monthly' ? 'active' : ''}`}
              onClick={() => setPeriod('monthly')}
            >
              Monthly
            </button>
          </div>
          {report.kids.length > 1 && (
            <select
              className="kid-selector"
              value={selectedKid}
              onChange={(e) => setSelectedKid(e.target.value)}
            >
              <option value="all">All Kids</option>
              {report.kids.map(kid => (
                <option key={kid.kid_user_id} value={kid.kid_user_id}>
                  {kid.kid_name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Household Summary */}
      <div className="household-summary">
        <div className="summary-card">
          <div className="summary-icon">✅</div>
          <div className="summary-content">
            <div className="summary-value">{report.total_tasks_completed}</div>
            <div className="summary-label">Tasks Completed</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">💰</div>
          <div className="summary-content">
            <div className="summary-value">{formatCurrency(report.total_earned)}</div>
            <div className="summary-label">Total Earned</div>
          </div>
        </div>
        {report.most_active_kid && (
          <div className="summary-card highlight">
            <div className="summary-icon">🏆</div>
            <div className="summary-content">
              <div className="summary-value">{report.most_active_kid}</div>
              <div className="summary-label">Most Active</div>
            </div>
          </div>
        )}
      </div>

      {/* Individual Kid Cards */}
      <div className="kids-progress">
        {report.kids.map(kid => (
          <div key={kid.kid_user_id} className="kid-progress-card">
            <div className="kid-header">
              <div className="kid-avatar">{kid.avatar || '👤'}</div>
              <div className="kid-info">
                <h3>{kid.kid_name}</h3>
                <div className="kid-streak">
                  {kid.current_streak > 0 && (
                    <>
                      <span className="streak-icon">🔥</span>
                      <span className="streak-count">{kid.current_streak} day streak</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="kid-metrics">
              <div className="metric-row">
                <div className="metric">
                  <span className="metric-label">Tasks Done</span>
                  <span className="metric-value">{kid.tasks_completed}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Pending</span>
                  <span className="metric-value pending">{kid.tasks_pending}</span>
                </div>
              </div>

              <div className="metric-row">
                <div className="metric">
                  <span className="metric-label">Earned</span>
                  <span className="metric-value earned">{formatCurrency(kid.total_earned)}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Balance</span>
                  <span className="metric-value">{formatCurrency(kid.current_balance)}</span>
                </div>
              </div>

              {kid.completion_rate > 0 && (
                <div className="completion-rate">
                  <div className="rate-label">Completion Rate</div>
                  <div className="rate-bar-container">
                    <div
                      className="rate-bar-fill"
                      style={{ width: `${kid.completion_rate}%` }}
                    />
                  </div>
                  <div className="rate-value">{kid.completion_rate.toFixed(0)}%</div>
                </div>
              )}

              <div className="metric-row">
                <div className="metric">
                  <span className="metric-label">Goals Active</span>
                  <span className="metric-value">{kid.goals_in_progress}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Goals Done</span>
                  <span className="metric-value success">{kid.goals_completed}</span>
                </div>
              </div>

              {kid.achievements_earned > 0 && (
                <div className="achievements-badge">
                  <span className="badge-icon">🏆</span>
                  <span className="badge-text">
                    {kid.achievements_earned} achievement{kid.achievements_earned !== 1 ? 's' : ''} earned!
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {report.kids.length === 0 && (
        <div className="no-data">
          <p>No activity data for this period</p>
        </div>
      )}
    </div>
  );
};

export default ProgressReports;
